import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CalendarDays,
  ExternalLink,
  Filter,
  Plus,
  Settings2,
  Users,
} from "lucide-react";

import { ApiError, formatDate } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import {
  Badge,
  Button,
  Card,
  Cargando,
  ErrorCarga,
  Input,
  PageHeader,
  Select,
  TableWrap,
  Textarea,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import {
  crearReunion,
  crearTipo,
  listarReuniones,
  listarTipos,
  type EstadoReunion,
  type ReunionRead,
  type TipoReunionRead,
} from "./api";
import { EstadoReunionBadge } from "./estado";

const FILTROS: { value: EstadoReunion | "TODAS"; label: string }[] = [
  { value: "TODAS", label: "Todas" },
  { value: "Programada", label: "Programadas" },
  { value: "Realizada", label: "Realizadas (asistencia parcial)" },
  { value: "Cerrada", label: "Cerradas (multas generadas)" },
  { value: "Cancelada", label: "Canceladas" },
];

export function AsambleasListPage(): JSX.Element {
  const [filtro, setFiltro] = useState<EstadoReunion | "TODAS">("TODAS");
  const [openForm, setOpenForm] = useState(false);
  const [openTipos, setOpenTipos] = useState(false);

  const tiposQ = useQuery({ queryKey: ["tipos-reunion"], queryFn: listarTipos });
  const reunionesQ = useQuery({
    queryKey: ["reuniones", filtro],
    queryFn: () => listarReuniones(filtro === "TODAS" ? undefined : filtro),
  });

  const tipoById = new Map<number, TipoReunionRead>();
  for (const t of tiposQ.data ?? []) tipoById.set(t.id, t);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Asambleas"
        icon={CalendarDays}
        subtitle={
          <>
            Reuniones del comité con carga de asistencia en lote y multas por
            inasistencia.
            {reunionesQ.data && <> ({reunionesQ.data.length} reuniones)</>}
          </>
        }
        actions={
          <>
            <Button
              variant="secondary"
              icon={Settings2}
              onClick={() => setOpenTipos((v) => !v)}
            >
              Tipos
            </Button>
            <Button
              icon={Plus}
              onClick={() => setOpenForm((v) => !v)}
              disabled={!tiposQ.data || tiposQ.data.length === 0}
              title={
                !tiposQ.data || tiposQ.data.length === 0
                  ? "Crea primero un tipo de reunión"
                  : "Crear reunión"
              }
            >
              Nueva reunión
            </Button>
          </>
        }
      />
      {/* Asambleas v2 (Fase 5): la asamblea en vivo — marcado desde el celular,
          poderes con firma, proyección y votaciones — vive en su propia app. Acá
          queda la carga en lote para reuniones que no se hacen en vivo. */}
      <a
        href="https://asambleas.innovaclarke.cl"
        target="_blank"
        rel="noreferrer"
        className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-800 hover:bg-brand-100"
      >
        <span className="min-w-0 flex-1">
          <b>Asamblea en vivo:</b> marcado desde el celular, poderes con firma,
          pantalla de sala y votaciones se hacen en InnovaClarke Asambleas.
        </span>
        <span className="inline-flex shrink-0 items-center gap-1 font-semibold">
          asambleas.innovaclarke.cl <ExternalLink className="h-3.5 w-3.5" />
        </span>
      </a>
      {openTipos && (
        <TiposReunionPanel
          tipos={tiposQ.data ?? []}
          loading={tiposQ.isPending}
        />
      )}

      {openForm && (
        <CrearReunionForm
          tipos={tiposQ.data ?? []}
          onClose={() => setOpenForm(false)}
        />
      )}

      <Card padding="sm">
        <div className="flex items-center gap-2 text-sm">
          <Filter className="h-4 w-4 text-neutral-500" strokeWidth={1.5} />
          <select
            value={filtro}
            onChange={(e) =>
              setFiltro(e.target.value as EstadoReunion | "TODAS")
            }
            className="rounded-md border border-neutral-450 px-3 py-1.5 text-sm shadow-sm"
          >
            {FILTROS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
      </Card>

      {reunionesQ.isPending && <Cargando />}
      {reunionesQ.isError && (
        <ErrorCarga
          error={reunionesQ.error}
          mensaje="Error al cargar reuniones."
          onReintentar={() => void reunionesQ.refetch()}
        />
      )}

      {reunionesQ.data && reunionesQ.data.length === 0 && (
        <p className="text-sm text-neutral-500">
          {filtro === "TODAS"
            ? "Sin reuniones registradas."
            : `Sin reuniones en estado ${filtro}.`}
        </p>
      )}

      {reunionesQ.data && reunionesQ.data.length > 0 && (
        <>
          {/* Desktop: tabla. */}
          <TableWrap label="Reuniones" className="hidden md:block">
            <table className="min-w-full divide-y divide-neutral-200 text-sm">
              <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th scope="col" className="px-3 py-2 text-left">Fecha</th>
                  <th scope="col" className="px-3 py-2 text-left">Tipo</th>
                  <th scope="col" className="px-3 py-2 text-left">Lugar</th>
                  <th scope="col" className="px-3 py-2 text-left">Multa</th>
                  <th scope="col" className="px-3 py-2 text-left">Estado</th>
                  <th scope="col" className="px-3 py-2 text-right">{""}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {reunionesQ.data.map((r) => (
                  <ReunionRow
                    key={r.id}
                    reunion={r}
                    tipo={tipoById.get(r.tipo_reunion_id)}
                  />
                ))}
              </tbody>
            </table>
          </TableWrap>

          {/* Mobile: tarjetas tappeables (toda la card es el link). */}
          <ul className="md:hidden space-y-2">
            {reunionesQ.data.map((r) => (
              <ReunionCardMobile
                key={r.id}
                reunion={r}
                tipo={tipoById.get(r.tipo_reunion_id)}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function ReunionRow({
  reunion,
  tipo,
}: {
  reunion: ReunionRead;
  tipo: TipoReunionRead | undefined;
}): JSX.Element {
  return (
    <tr className="hover:bg-neutral-50">
      <td className="px-3 py-2">
        <div className="font-medium text-neutral-900">
          {formatDate(reunion.fecha)}
        </div>
        <div className="text-xs text-neutral-500">{reunion.hora.slice(0, 5)}</div>
      </td>
      <td className="px-3 py-2 text-neutral-700">
        {tipo?.nombre ?? `tipo #${reunion.tipo_reunion_id}`}
      </td>
      <td className="px-3 py-2 text-neutral-600">{reunion.lugar ?? "—"}</td>
      <td className="px-3 py-2">
        {reunion.genera_multa ? (
          <Badge tone="amber">Sí</Badge>
        ) : (
          <Badge tone="slate">No</Badge>
        )}
      </td>
      <td className="px-3 py-2">
        <EstadoReunionBadge estado={reunion.estado} />
      </td>
      <td className="px-3 py-2 text-right">
        <Link
          to={`/asambleas/${reunion.id}`}
          className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:text-brand-800"
        >
          Detalle
          <ArrowRight className="h-3 w-3" />
        </Link>
      </td>
    </tr>
  );
}

function ReunionCardMobile({
  reunion,
  tipo,
}: {
  reunion: ReunionRead;
  tipo: TipoReunionRead | undefined;
}): JSX.Element {
  return (
    <li>
      <Link
        to={`/asambleas/${reunion.id}`}
        className="block rounded-lg bg-white p-3 ring-1 ring-neutral-200 active:bg-neutral-50 hover:bg-neutral-50"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-medium text-neutral-900">
              {tipo?.nombre ?? `Tipo #${reunion.tipo_reunion_id}`}
            </div>
            <div className="text-xs text-neutral-500 mt-0.5">
              {formatDate(reunion.fecha)} · {reunion.hora.slice(0, 5)}
            </div>
            {reunion.lugar && (
              <div className="text-xs text-neutral-600 mt-1 truncate">
                📍 {reunion.lugar}
              </div>
            )}
          </div>
          <ArrowRight className="h-4 w-4 text-neutral-500 mt-1 shrink-0" strokeWidth={1.5} />
        </div>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <EstadoReunionBadge estado={reunion.estado} />
          {reunion.genera_multa ? (
            <Badge tone="amber">Multa</Badge>
          ) : (
            <Badge tone="slate">Sin multa</Badge>
          )}
        </div>
      </Link>
    </li>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Forms
// ────────────────────────────────────────────────────────────────────────

function CrearReunionForm({
  tipos,
  onClose,
}: {
  tipos: TipoReunionRead[];
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [tipoId, setTipoId] = useState<string>("");
  const [fecha, setFecha] = useState("");
  const [hora, setHora] = useState("19:00");
  const [lugar, setLugar] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [generaMulta, setGeneraMulta] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: crearReunion,
    onSuccess: (r) => {
      toast.success(`Reunión #${r.id} creada (${formatDate(r.fecha)})`);
      void qc.invalidateQueries({ queryKey: ["reuniones"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo crear";
      setError(msg);
      toast.error(msg);
    },
  });

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([tipoId, fecha, hora, lugar, observaciones, generaMulta]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mutation.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Crear reunión"
      icon={CalendarDays}
      size="lg"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={intentarCerrar}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              setError(null);
              if (!tipoId) return setError("Elige un tipo");
              if (!fecha || !hora)
                return setError("Fecha y hora son obligatorias");
              mutation.mutate({
                tipo_reunion_id: Number(tipoId),
                fecha,
                hora: hora.length === 5 ? `${hora}:00` : hora,
                lugar: lugar.trim() || null,
                observaciones: observaciones.trim() || null,
                genera_multa: generaMulta,
              });
            }}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? "Creando…" : "Crear"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Select
          label="Tipo"
          value={tipoId}
          onChange={(e) => setTipoId(e.target.value)}
          disabled={mutation.isPending}
        >
          <option value="">— elegir —</option>
          {tipos.map((t) => (
            <option key={t.id} value={t.id}>
              {t.nombre}
            </option>
          ))}
        </Select>
        <Input
          label="Fecha"
          type="date"
          value={fecha}
          onChange={(e) => setFecha(e.target.value)}
          disabled={mutation.isPending}
        />
        <Input
          label="Hora"
          type="time"
          value={hora}
          onChange={(e) => setHora(e.target.value)}
          disabled={mutation.isPending}
        />
      </div>
      <Input
        label="Lugar"
        type="text"
        maxLength={256}
        value={lugar}
        onChange={(e) => setLugar(e.target.value)}
        placeholder="ej. Sede social"
        disabled={mutation.isPending}
      />
      <Textarea
        label="Observaciones"
        rows={2}
        value={observaciones}
        onChange={(e) => setObservaciones(e.target.value)}
        placeholder="ej. Renovación de directiva"
        disabled={mutation.isPending}
      />
      <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
        <input
          type="checkbox"
          checked={generaMulta}
          onChange={(e) => setGeneraMulta(e.target.checked)}
          disabled={mutation.isPending}
        />
        Genera multa por inasistencia (reunión obligatoria)
      </label>

      {error && <p className="text-sm text-rose-700">{error}</p>}
      </div>
      {dialogoDescarte}
    </Modal>
  );
}

function TiposReunionPanel({
  tipos,
  loading,
}: {
  tipos: TipoReunionRead[];
  loading: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <Card className="space-y-3">
      <header className="flex items-center gap-2">
        <Settings2 className="h-5 w-5 text-neutral-500" strokeWidth={1.5} />
        <h3 className="text-sm font-semibold text-neutral-900">
          Tipos de reunión
        </h3>
        <Users className="ml-auto h-4 w-4 text-neutral-500" strokeWidth={1.5} />
        <span className="text-xs text-neutral-500">{tipos.length} tipo(s)</span>
        <Button size="sm" icon={Plus} onClick={() => setOpen(true)}>
          Nuevo tipo
        </Button>
      </header>

      {loading ? (
        <Cargando inline />
      ) : tipos.length === 0 ? (
        <p className="text-sm text-neutral-500">
          Sin tipos cargados. Necesitas al menos uno para crear reuniones.
        </p>
      ) : (
        <ul className="text-sm divide-y divide-neutral-100">
          {tipos.map((t) => (
            <li key={t.id} className="py-1.5">
              <span className="font-medium text-neutral-900">{t.nombre}</span>
              {t.descripcion && (
                <span className="ml-2 text-xs text-neutral-500">
                  {t.descripcion}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {open && <TipoReunionModal onClose={() => setOpen(false)} />}
    </Card>
  );
}

function TipoReunionModal({ onClose }: { onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: crearTipo,
    onSuccess: (t) => {
      toast.success(`Tipo "${t.nombre}" creado`);
      void qc.invalidateQueries({ queryKey: ["tipos-reunion"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo crear";
      setError(msg);
      toast.error(msg);
    },
  });

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([nombre, descripcion]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mutation.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Nuevo tipo de reunión"
      icon={Settings2}
      size="md"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={intentarCerrar}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              setError(null);
              if (!nombre.trim()) return setError("Nombre requerido");
              mutation.mutate({
                nombre: nombre.trim(),
                descripcion: descripcion.trim() || null,
              });
            }}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? "Creando…" : "Crear tipo"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Nombre"
          type="text"
          maxLength={128}
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="ej. Asamblea Ordinaria"
          disabled={mutation.isPending}
          autoFocus
        />
        <Input
          label="Descripción (opcional)"
          type="text"
          maxLength={256}
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          disabled={mutation.isPending}
        />
        {error && <p className="text-xs text-rose-700">{error}</p>}
      </div>
      {dialogoDescarte}
    </Modal>
  );
}
